import { withOpacity } from "@common/helper";
import { THEME_COLORS } from "@common/themes";
import styled from "@emotion/styled";
import { ArrowDropDown, Clear, InfoRounded } from "@mui/icons-material";
import { Box, IconButton, Popover } from "@mui/material";
import React, { useEffect, useState } from "react";
import AppText from "../AppText";
import TooltipV3 from "../TooltipV3";

const InputContainerBox = styled.div`
  border: 1px solid ${withOpacity(THEME_COLORS.black, 0.2)};
  width: 150px;
  padding: 8px 16px;
  border-radius: 4px;
  box-sizing: border-box;
  position: relative;
  display: flex;
  padding-right: calc(1em + 16px);
`;

const PopoverContent = styled.div`
  width: 330px;
  background-color: white;
`;

const DashedLine = styled(Box)`
  flex-grow: 1;
  border-top: 2px dashed ${withOpacity("#7D7D7D", 0.3)};
`;

const Divider = styled.div`
  background-color: ${withOpacity("#7D7D7D", 0.2)};
  width: 100%;
  height: 1px;
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 20px;
  column-gap: 16px;
`;

const Button = styled.div`
  color: black;
  opacity: 0.9;
  font-family: "NotoSansMedium";
  font-size: 14px;
  transition: color 0.1s;
  cursor: pointer;
  user-select: none;
  &:hover {
    color: ${THEME_COLORS.purple};
  }
`;

const RangeInputContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
`;

const RangeTextInput = styled(Box)`
  width: 100px;
  flex-direction: row;
  display: flex;
  padding: 6px 8px;
  border-radius: 8px;
  box-sizing: border-box;
  column-gap: 2px;
  border: 2px solid #8233b5;
`;

const RangeInputSeparator = styled(Box)`
  height: 35px;
  display: flex;
  align-items: center;
  flex-direction: row;
  column-gap: 4px;
  flex: 1;
`;

const TopContainer = styled(Box)`
  padding: 20px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  column-gap: 4px;
`;

const StyledInput = styled.input`
  font-family: NotoSans;
  font-size: 14px;
  color: black;
  flex: 1;
  width: 100%;
  outline: none;
  border: none;
  padding: 0;
`;

const StyledIconButton = styled(IconButton)`
  position: absolute;
  top: -12px;
  right: -12px;
  background-color: ${THEME_COLORS.purple3};
  & svg {
    color: ${THEME_COLORS.purple};
  }
  &:hover {
    background-color: ${THEME_COLORS.purple};
    & svg {
      color: ${THEME_COLORS.white};
    }
  }
`;

const RangeInput = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
}) => (
  <RangeInputContainer>
    <AppText>{label}</AppText>
    <RangeTextInput>
      <StyledInput type="number" placeholder="0" min={0} value={value} onChange={onChange} />
      <AppText color="#7D7D7D">days</AppText>
    </RangeTextInput>
  </RangeInputContainer>
);

type RangeSelectorProps = {
  label?: string;
  value?: [number, number];
  onChange?: (newValue: [number, number] | null) => void;
};

const RangeSelector = ({ label, value, onChange }: RangeSelectorProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [minDays, setMinDays] = useState("");
  const [maxDays, setMaxDays] = useState("");

  const minDaysInt = parseInt(minDays || "0");
  const maxDaysInt = parseInt(maxDays || "0");

  useEffect(() => {
    if (!anchorEl) return;

    if (value) {
      const [min, max] = value;
      setMinDays(min + "");
      setMaxDays(max + "");
    }

    return () => {
      setMinDays("");
      setMaxDays("");
    };
  }, [value, anchorEl]);

  const handleClickInput = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClickCancel = () => {
    setAnchorEl(null);
    onChange?.(null);
  };

  const handleClearButton = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
    onChange?.(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMinDaysChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMinDays(event.target.value);
  };

  const handleMaxDaysChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMaxDays(event.target.value);
  };

  const handleClickApplyFilter = () => {
    const { error, valid } = validate();
    if (!valid) {
      alert(`Error: ${error}`);
      return;
    }
    onChange?.([minDaysInt, maxDaysInt]);
    setAnchorEl(null);
  };

  const validate = () => {
    let error = "";

    if (minDaysInt < 0 || maxDaysInt < 0) {
      error = "Min/Max days can't be negative.";
    } else if (minDaysInt === 0 && maxDaysInt === 0) {
      error = "Please input min/max days.";
    } else if (maxDaysInt !== 0 && maxDaysInt < minDaysInt) {
      error = "Max days can't be less than min days.";
    }
    return { error, valid: !error };
  };

  const displayLabel = value ? `${value[0]} to ${value[1] === 0 ? "∞" : value[1]} days` : label;

  return (
    <>
      <TooltipV3 title={label}>
        <InputContainerBox onClick={handleClickInput}>
          <Box width="100%">
            <AppText numberOfLines={1}>{displayLabel}</AppText>
          </Box>
          <ArrowDropDown
            style={{ color: THEME_COLORS.purple, position: "absolute", alignSelf: "center", right: "7px" }}
          />
          {!!value && (
            <StyledIconButton size="small" onClick={handleClearButton}>
              <Clear fontSize="small" style={{ fontSize: 14 }} />
            </StyledIconButton>
          )}
        </InputContainerBox>
      </TooltipV3>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <PopoverContent>
          <TopContainer>
            <RangeInput label="Min days" value={minDays} onChange={handleMinDaysChange} />
            <RangeInputSeparator>
              <DashedLine />
              <AppText color="#7D7D7D">to</AppText>
              <DashedLine />
            </RangeInputSeparator>
            <RangeInput label="Max days" value={maxDays} onChange={handleMaxDaysChange} />
          </TopContainer>
          <Box
            display="flex"
            justifyContent="center"
            marginBottom="16px"
            columnGap="4px"
            alignItems="flex-start"
            paddingX="16px"
          >
            <InfoRounded sx={{ fontSize: "14px", color: "#3E7DC3", marginTop: "2px" }} />
            <AppText fontSize={11} color="#3E7DC3">
              Leave the max days empty to indicate an unlimited range.
            </AppText>
          </Box>
          <Divider />
          <ButtonsContainer>
            <Button onClick={handleClickCancel}>Cancel</Button>
            <Button onClick={handleClickApplyFilter}>Apply filter</Button>
          </ButtonsContainer>
        </PopoverContent>
      </Popover>
    </>
  );
};

export default RangeSelector;
